import { useCallback, useMemo } from "react";
import { Select } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { CustomModelValue } from "@models/ISchema";
import { CustomModelValueOption } from "@services/types/custom-models/customModelsTypes";
import { useGetAllCustomModelValues } from "@services/collections/custom-models/customModelsServices";
import styles from "./campusCodeSelector.module.scss";

const CAMPUSES_CUSTOM_MODEL_CODE = "CAMPUSES";

/**
 * Value of the synthetic "select all" entry. Real options carry the custom
 * model value `id`, so a bracketed non-id string cannot collide with one.
 */
const SELECT_ALL_OPTION_VALUE = "[[select-all-campuses]]";

interface SelectAllOption {
  label: string;
  value: string;
  self: null;
}

type CampusSelectorOption = CustomModelValueOption | SelectAllOption;

interface CampusCodeSelectorProps {
  scenario: string;
  origin?: string;
  /** Campus codes currently held by the form, saved and session-added alike. */
  selectedCampusCodes: string[];
  isDisabled?: boolean;
  /** Called with the code of a single campus picked from the list. */
  onSelectCampus: (campusCode: string) => void;
  /** Called with every loaded campus code when the select-all entry is used. */
  onSelectAllCampuses: (campusCodes: string[]) => void;
}

const isSelectAllOption = (option: CampusSelectorOption): option is SelectAllOption =>
  option?.value === SELECT_ALL_OPTION_VALUE;

const getOptionLabel = (customModelValue: CustomModelValue) =>
  `${customModelValue?.code} | ${customModelValue?.label}`;

const CampusCodeSelector = ({
  scenario,
  origin,
  selectedCampusCodes,
  isDisabled,
  onSelectCampus,
  onSelectAllCampuses,
}: CampusCodeSelectorProps) => {
  const { t } = useTranslation();

  const { options: campusOptions, loading } = useGetAllCustomModelValues({
    scenario,
    origin,
    customModelCode: CAMPUSES_CUSTOM_MODEL_CODE,
    getOptionLabel,
  });

  const selectedCodes = useMemo(() => new Set(selectedCampusCodes), [selectedCampusCodes]);

  /**
   * Nothing left to add: every loaded campus is already in the form. Guarded on
   * a non-empty list so an unresolved request is not read as "all selected".
   */
  const isSelectAllDisabled = useMemo(() => {
    if (loading || !campusOptions.length) return true;

    return campusOptions.every(option => selectedCodes.has(option?.self?.code));
  }, [loading, campusOptions, selectedCodes]);

  const options = useMemo<CampusSelectorOption[]>(() => {
    const selectAllOption: SelectAllOption = {
      label: t("programming-structures.form.campuses.select-all"),
      value: SELECT_ALL_OPTION_VALUE,
      self: null,
    };

    return [selectAllOption, ...campusOptions];
  }, [t, campusOptions]);

  /**
   * The select-all entry stays visible while the user searches: it always adds
   * every campus, not the filtered subset, as its label states.
   */
  const filterOption = useCallback(
    (option: { data: CampusSelectorOption; label: string }, searchTerm: string) => {
      if (isSelectAllOption(option?.data)) return true;

      return option?.label?.toLowerCase().includes(searchTerm?.toLowerCase() ?? "");
    },
    [],
  );

  const isOptionDisabled = useCallback(
    (option: CampusSelectorOption) => {
      if (isSelectAllOption(option)) return isSelectAllDisabled;

      return selectedCodes.has((option as CustomModelValueOption)?.self?.code);
    },
    [isSelectAllDisabled, selectedCodes],
  );

  const formatOptionLabel = useCallback((option: CampusSelectorOption) => {
    return option?.label;
  }, []);

  const handleChange = useCallback(
    (option: CampusSelectorOption) => {
      if (!option) return;

      if (isSelectAllOption(option)) {
        onSelectAllCampuses(campusOptions.map(campusOption => campusOption?.self?.code));
        return;
      }

      onSelectCampus((option as CustomModelValueOption)?.self?.code);
    },
    [campusOptions, onSelectCampus, onSelectAllCampuses],
  );

  return (
    <Select
      className={styles.campusCodeSelector}
      label={t("advanced-search.classroom.forms.campus.select-value.input-label")}
      placeholder={t("advanced-search.classroom.forms.campus.select-value.placeholder")}
      /* Kept null so a pick never sticks as the rendered value: this selector
         feeds a tag list, it does not hold a single selection. */
      value={null}
      options={options}
      /* Avocado's `Select` sets react-select's `isDisabled` from its own
         `disabled` prop after spreading the rest, so `disabled` is the one
         that takes effect. */
      disabled={isDisabled}
      isLoading={loading}
      filterOption={filterOption}
      isOptionDisabled={isOptionDisabled}
      formatOptionLabel={formatOptionLabel}
      onChange={handleChange}
    />
  );
};

export default CampusCodeSelector;
